import {
  QUESTIONS,
  STEP_IDS_OBFUSCATED,
} from '@sleepstation/const-dbas-questionnaire'
import {
  propFlipped,
  replaceTokens,
  splitOnComma,
  swapKeysAndValues,
} from '@sleepstation/utils'
import {
  assoc,
  clamp,
  concat,
  curry,
  ifElse,
  map,
  objOf,
  pipe,
  replace,
} from 'ramda'
import { isNotNilOrEmpty, renameKeysWith } from 'ramda-adjunct'
import {
  DBAS_LEVEL,
  DBAS_SCORE_MAX,
  DBAS_SCORE_MIN,
  EPWORTH_LEVEL,
  EPWORTH_SCORE_MAX,
  EPWORTH_SCORE_MIN,
} from '../const/quiz'
import localStorageService from '../services/localStorageService'

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const clampEpworthScore = clamp(EPWORTH_SCORE_MIN, EPWORTH_SCORE_MAX)

const clampDbasScore = clamp(DBAS_SCORE_MIN, DBAS_SCORE_MAX)

const getDeobfuscatedDbasIds = (ids) => {
  const reversedMap = swapKeysAndValues(STEP_IDS_OBFUSCATED)
  return map(propFlipped(reversedMap), ids)
}

const lookupFullQuestions = map(propFlipped(QUESTIONS))

const buildPayload = (userData, namespace, score, quizName) => {
  // Conditioning needed, as questions include markdown, such as bold * or new line
  // Also Epworth answer is formatted with underscores only.
  // Code works for both quizNames, but can split that in cases for each quizName, or better:
  // might be worth outputting correctly formatted data at component level
  // as conditioning might cause issues with future questionnaires or if current ones are changed
  const returnValue = (item) => {
    const conditionedQuestion = replace(/\s\n\s+|\*/g, ``, item.step.question)
    const conditionedAnswer = replace(/_/g, ` `, item.answer.toString()) // The toString here is only for DBAS answers - I think there might be a minor bug in the monorepo
    return { question: conditionedQuestion, answer: conditionedAnswer }
  }

  const mappedAnswers = map(returnValue, userData)
  const mappedAnswersAndScore = assoc(`score`, score.toString(), mappedAnswers)
  const prependString = `portal_${namespace}_${quizName}_`
  const mappedKeysAnswersAndScore = renameKeysWith(
    concat(prependString),
    mappedAnswersAndScore
  )

  return mappedKeysAnswersAndScore
}

// -----------------------------------------------------------------------------
// Exports
// -----------------------------------------------------------------------------

export const patchQuizData = curry(
  (namespace, api, { userData, score, quizName }) => {
    const { putOnboardingData, patchOnboardingData } = api
    const localStorageApi = localStorageService()
    const payload = buildPayload(userData, namespace, score, quizName)

    // Check if user has not submitted to the onboarding API yet (use put and
    // create token) or has already submitted (use patch, with the existing
    // token)
    if (localStorageApi.doesNotHaveValue(`onboardingToken`)) {
      putOnboardingData(payload)
        .then((result) => {
          const onboardingToken = result.data.data.hash
          localStorageApi.setValue(`onboardingToken`, onboardingToken)
        })
        .catch(() => {
          // no-op
          // If we can't persist the data we don't want to prevent the user from
          // progressing.
        })
    } else {
      const onboardingToken = localStorageApi.getString(`onboardingToken`)
      patchOnboardingData(onboardingToken, payload).catch(() => {
        // Do nothing
        // If we can't persist the data we don't want to prevent the user from
        // progressing.
      })
    }
  }
)

export const getEpworthLevel = (score) => {
  if (score < 11) return EPWORTH_LEVEL.POSITIVE
  if (score > 15) return EPWORTH_LEVEL.NEGATIVE
  return EPWORTH_LEVEL.NEUTRAL
}

export const getDbasLevel = (score, questions) => {
  const isFlagged = isNotNilOrEmpty(questions)
  if (score >= 4) return DBAS_LEVEL.HIGH
  if (isFlagged) return DBAS_LEVEL.LOW_FLAGGED
  return DBAS_LEVEL.LOW
}

export const getEpworthScoreFormatted = pipe(
  clampEpworthScore,
  objOf(`score`),
  replaceTokens(`#{score}/${EPWORTH_SCORE_MAX}`)
)

export const getDbasScoreFormatted = pipe(
  clampDbasScore,
  objOf(`score`),
  replaceTokens(`#{score}/${DBAS_SCORE_MAX}`)
)

export const getObfuscatedDbasIds = map(propFlipped(STEP_IDS_OBFUSCATED))

export const getFullQuestions = pipe(
  ifElse(isNotNilOrEmpty, splitOnComma, () => []),
  getDeobfuscatedDbasIds,
  lookupFullQuestions
)
